import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Upload, Edit, Trash2, Download } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { uploadImages } from "@/components/ImageManager";
import { api, resolveImage, formatPrice, formatError } from "@/lib/api";

const EMPTY = {
  title: "", description: "", cover_url: null, storage_path: "", size: 0,
  is_free_public: false, is_gift: false, is_paid: false, price: 0.5, active: true,
};

const TypeBadges = ({ p }) => (
  <div className="flex flex-wrap gap-1">
    {p.is_free_public && <Badge className="bg-emerald-600 text-white text-[10px]">Gratuito pubblico</Badge>}
    {p.is_gift && <Badge className="bg-brand-yellow text-brand-blue text-[10px]">Omaggio checkout</Badge>}
    {p.is_paid && <Badge className="bg-brand-blue text-white text-[10px]">{formatPrice(p.price)} al checkout</Badge>}
    {!p.active && <Badge variant="secondary" className="text-[10px]">Disattivato</Badge>}
  </div>
);

const Toggle = ({ label, k, form, set }) => (
  <label className="flex items-center justify-between gap-3 bg-slate-50 rounded-xl px-3 py-2 text-sm font-bold text-slate-700">
    {label}
    <Switch data-testid={`pdf-${k}`} checked={!!form[k]} onCheckedChange={(v) => set(k, v)} />
  </label>
);

export default function AdminPdfs() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState("");

  const load = async () => setList((await api.get("/admin/pdfs")).data);
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => { setForm(EMPTY); setEditId(null); setFileName(""); };

  const uploadPdf = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/pdfs/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, storage_path: data.storage_path, size: data.size, title: f.title || file.name.replace(/\.pdf$/i, "") }));
      setFileName(file.name);
      toast.success("PDF caricato");
    } catch (e) {
      toast.error("Upload fallito", { description: formatError(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    try {
      const [url] = await uploadImages([file]);
      set("cover_url", url);
    } catch (e) {
      toast.error("Upload copertina fallito", { description: formatError(e.response?.data?.detail) || e.message });
    }
  };

  const save = async () => {
    if (!form.title.trim() || !form.storage_path) { toast.error("Titolo e file PDF obbligatori"); return; }
    if (!form.is_free_public && !form.is_gift && !form.is_paid) { toast.error("Scegli almeno un tipo"); return; }
    const payload = { ...form, price: parseFloat(form.price) || 0.5 };
    try {
      if (editId) await api.put(`/admin/pdfs/${editId}`, payload);
      else await api.post("/admin/pdfs", payload);
      toast.success(editId ? "PDF aggiornato" : "PDF aggiunto");
      reset();
      load();
    } catch (e) {
      toast.error("Errore salvataggio", { description: formatError(e.response?.data?.detail) || e.message });
    }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo PDF? Chi l'ha gi\u00e0 acquistato potr\u00e0 ancora scaricarlo.")) return;
    await api.delete(`/admin/pdfs/${id}`);
    toast.success("PDF eliminato");
    if (editId === id) reset();
    load();
  };

  const preview = async (p) => {
    const { data } = await api.get(`/admin/pdfs/${p.id}/file`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data);
    a.download = `${p.title}.pdf`;
    a.click();
  };

  const edit = (p) => { setEditId(p.id); setForm({ ...EMPTY, ...p }); setFileName("File gi\u00e0 caricato"); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <AdminLayout title="PDF digitali" back="/admin">
      <div className="bg-white border border-border rounded-2xl p-4 sm:p-6 space-y-4" data-testid="pdf-form">
        <h2 className="font-heading font-bold text-brand-blue">{editId ? "Modifica PDF" : "Nuovo PDF"}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-4">
          <label data-testid="pdf-cover-upload" className="aspect-[3/4] rounded-xl border-2 border-dashed border-brand-blue/40 flex flex-col items-center justify-center cursor-pointer overflow-hidden hover:bg-blue-50 transition-colors">
            {form.cover_url ? <img src={resolveImage(form.cover_url)} alt="" className="w-full h-full object-cover" /> : (
              <><Upload className="w-5 h-5 text-brand-blue" /><span className="text-[10px] font-bold text-brand-blue uppercase mt-1">Copertina</span></>
            )}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadCover(e.target.files[0])} />
          </label>
          <div className="space-y-3">
            <label data-testid="pdf-file-upload" className="flex items-center gap-3 border-2 border-dashed border-brand-blue/40 rounded-xl px-4 py-3 cursor-pointer hover:bg-blue-50 transition-colors">
              <FileText className="w-6 h-6 text-brand-blue" />
              <span className="text-sm font-bold text-brand-blue truncate">{busy ? "Caricamento..." : fileName || "Scegli file PDF (max 20MB)"}</span>
              <input data-testid="pdf-file-input" type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => uploadPdf(e.target.files[0])} />
            </label>
            <div>
              <Label>Titolo *</Label>
              <Input data-testid="pdf-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1 rounded-xl" />
            </div>
            <div>
              <Label>Descrizione</Label>
              <Textarea data-testid="pdf-description" value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1 rounded-xl" rows={3} />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Toggle label="Gratuito nella libreria pubblica" k="is_free_public" form={form} set={set} />
          <Toggle label="Omaggio al checkout (1 per ordine)" k="is_gift" form={form} set={set} />
          <Toggle label="A pagamento al checkout" k="is_paid" form={form} set={set} />
          <Toggle label="Attivo" k="active" form={form} set={set} />
        </div>
        {form.is_paid && (
          <div className="max-w-[200px]">
            <Label>Prezzo PDF (\u20ac)</Label>
            <Input data-testid="pdf-price" type="number" step="0.01" min="0.5" value={form.price} onChange={(e) => set("price", e.target.value)} className="mt-1 rounded-xl" />
          </div>
        )}
        <div className="flex gap-2 justify-end">
          {editId && <Button variant="outline" data-testid="pdf-cancel" onClick={reset} className="rounded-full">Annulla</Button>}
          <Button data-testid="pdf-save" onClick={save} disabled={busy} className="rounded-full bg-brand-yellow hover:bg-brand-yellow-dark text-brand-blue font-bold">
            {editId ? "Salva modifiche" : "Aggiungi PDF"}
          </Button>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {list.map((p) => (
          <div key={p.id} data-testid={`pdf-row-${p.id}`} className="bg-white border border-border rounded-2xl p-3 flex gap-3 items-center">
            <div className="w-12 h-16 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0 flex items-center justify-center">
              {p.cover_url ? <img src={resolveImage(p.cover_url)} alt="" className="w-full h-full object-cover" /> : <FileText className="w-6 h-6 text-slate-400" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-sm truncate">{p.title}</div>
              <TypeBadges p={p} />
            </div>
            <div className="flex gap-1">
              <button data-testid={`pdf-preview-${p.id}`} onClick={() => preview(p)} className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center"><Download className="w-4 h-4" /></button>
              <button data-testid={`pdf-edit-${p.id}`} onClick={() => edit(p)} className="w-9 h-9 rounded-lg bg-brand-blue text-white flex items-center justify-center"><Edit className="w-4 h-4" /></button>
              <button data-testid={`pdf-del-${p.id}`} onClick={() => del(p.id)} className="w-9 h-9 rounded-lg bg-red-500 text-white flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-center text-slate-500 py-8" data-testid="pdf-empty">Nessun PDF caricato</p>}
      </div>
    </AdminLayout>
  );
}
